import { el } from './dom';
import { icon } from './Icons';
import type { UiPreferences } from './UiPreferences';

/**
 * Bottom status bar (spec §19): active tool and hint, selection/plant counts, units, and frame rate.
 * Also hosts the placement/selection readout the editor writes into, so measurements appear in the
 * SketchUp position rather than floating over the canvas.
 */

export interface StatusBarState {
  tool: string;
  hint: string;
  selectionCount: number;
  plantCount: number;
  speciesCount: number;
  fps: number;
}

/** Autosave indicator content. `tone` drives the colour; an empty label hides the element. */
export interface SaveIndicator {
  label: string;
  title: string;
  tone: 'quiet' | 'busy' | 'error';
}

export class StatusBar {
  readonly root: HTMLElement;
  /** The element the editor writes placement/selection measurements into. */
  readonly readout: HTMLElement;

  private readonly toolEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly countsEl: HTMLElement;
  private readonly saveEl: HTMLElement;
  private readonly unitButton: HTMLButtonElement;
  private readonly fpsEl: HTMLElement;

  /**
   * `existingReadout` lets the app adopt the element it already handed to the editor, so placement
   * measurements land in the status bar instead of floating over the canvas.
   */
  constructor(
    private readonly preferences: UiPreferences,
    private readonly onUnitChange: () => void,
    existingReadout?: HTMLElement,
  ) {
    this.toolEl = el('span', { class: 'lg-status__tool' });
    this.hintEl = el('span', { class: 'lg-status__hint' });
    this.readout = existingReadout ?? el('span', { class: 'lg-readout' });
    this.readout.classList.add('lg-status__readout');
    this.countsEl = el('span', { class: 'lg-status__counts' });
    // The save indicator says what is happening to the document; *which* document it is belongs to
    // the brand block in the toolbar, so the name is not repeated at both ends of the screen.
    this.saveEl = el('span', { class: 'lg-status__save', attrs: { hidden: true } });
    this.fpsEl = el('span', { class: 'lg-status__fps' });

    this.unitButton = el('button', {
      class: 'lg-status__unit',
      attrs: { type: 'button', title: 'Toggle display units' },
      on: {
        click: () => {
          this.preferences.setUnit(this.preferences.unit === 'meters' ? 'feet' : 'meters');
          this.syncUnit();
          this.onUnitChange();
        },
      },
    });
    this.syncUnit();

    this.root = el(
      'footer',
      { class: 'lg-status', attrs: { role: 'status' } },
      el('span', { class: 'lg-status__icon', html: icon('info', 14) }),
      this.toolEl,
      this.hintEl,
      this.readout,
      el('span', { class: 'lg-status__spacer' }),
      this.saveEl,
      this.countsEl,
      this.unitButton,
      this.fpsEl,
    );
  }

  /**
   * Show the autosave state. Kept out of {@link update} because it changes on its own schedule —
   * the twice-a-second status poll would otherwise blank it between saves.
   */
  setSaveIndicator(indicator: SaveIndicator): void {
    this.saveEl.textContent = indicator.label;
    this.saveEl.title = indicator.title;
    this.saveEl.hidden = indicator.label === '';
    this.saveEl.classList.toggle('is-busy', indicator.tone === 'busy');
    this.saveEl.classList.toggle('is-error', indicator.tone === 'error');
  }

  private syncUnit(): void {
    this.unitButton.textContent = this.preferences.unit === 'meters' ? 'meters' : 'feet';
  }

  update(state: StatusBarState): void {
    this.toolEl.textContent = state.tool;
    this.hintEl.textContent = state.hint;
    this.countsEl.textContent =
      `${state.selectionCount} selected  ·  ${state.plantCount} plants  ·  ${state.speciesCount} species`;
    this.fpsEl.textContent = `${Math.round(state.fps)} fps`;
  }

  dispose(): void {
    this.root.remove();
  }
}
